import { z } from 'zod'
import { calendarDaySchema, instantSchema } from '../validators/temporal.validator.js'
import { reviewerRoleSchema } from '../enums/reviewer-role.enum.js'
import { vendorReviewStatusSchema } from '../enums/vendor-review-status.enum.js'
import { vendorReviewReplyStatusSchema } from '../enums/vendor-review-reply-status.enum.js'
import { vendorServiceSchema } from '../enums/vendor-service.enum.js'

/**
 * Reviews of vendor companies by the couples and planners of the events they
 * worked (docs/vendor-growth.spec.md, "Reviews").
 *
 * What anyone but a platform admin sees of a review is a closed list: its
 * stars and comments, the reviewer as shown ("Jamie R.", or a planning
 * company's name), their role, and the event's month. Never the reviewer's full
 * name or email, nor the event's name, date or venue.
 */

/** How a reviewer is shown: "Jamie R." and a couple, or a company's name and a planner. */
export const reviewerDtoSchema = z.object({
  name: z.string(),
  role: reviewerRoleSchema,
})

export type ReviewerDto = z.infer<typeof reviewerDtoSchema>

/** "2026-10": only the month of the event is ever shown. */
export const reviewMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/)

/** The reader's review of one company on the event: theirs, or their planning company's. */
export const ownEventReviewDtoSchema = z.object({
  rating: z.number().int().min(1).max(5),
  body: z.string(),
  status: vendorReviewStatusSchema,
  /** Why an admin declined it; null unless declined. */
  declineReason: z.string().nullable(),
  sentAt: instantSchema,
  /** Who at the planning company last sent it; null for a couple's own. */
  writtenBy: z.string().nullable(),
})

export type OwnEventReviewDto = z.infer<typeof ownEventReviewDtoSchema>

/** One company that worked the event, and the reader's review of it. */
export const eventReviewTargetDtoSchema = z.object({
  vendorId: z.uuid(),
  companyName: z.string(),
  /** What it was there for, in the app's order: its assignments' services, and furniture for its rentals. */
  services: z.array(vendorServiceSchema),
  mine: ownEventReviewDtoSchema.nullable(),
  /**
   * Its review can no longer be written or changed: the company has left CS
   * Venue View, or is no longer on the event. Listed only when the reader
   * already reviewed it, so they still see where that review stands.
   */
  closed: z.boolean().default(false),
})

export type EventReviewTargetDto = z.infer<typeof eventReviewTargetDtoSchema>

/** The event's companies to review, for its couple or its planner. */
export const eventReviewsDtoSchema = z.object({
  /** Over and not canceled: reviews may be written. */
  open: z.boolean(),
  /** How the reader's reviews show. */
  shownAs: reviewerDtoSchema,
  /** The month they show under; null while the event has no date. */
  month: reviewMonthSchema.nullable(),
  /** By name. A planner's own company is never here. */
  vendors: z.array(eventReviewTargetDtoSchema),
})

export type EventReviewsDto = z.infer<typeof eventReviewsDtoSchema>

/**
 * A review waiting for approval, as a platform admin decides it: with what only
 * they see, the venue, the date, and who sent it, to write to if needed.
 */
export const reviewToApproveDtoSchema = z.object({
  id: z.uuid(),
  vendor: z.object({ id: z.uuid(), name: z.string() }),
  reviewer: reviewerDtoSchema,
  month: reviewMonthSchema,
  /** Who last sent it; null once their account is gone. */
  writtenBy: z.object({ name: z.string(), email: z.string() }).nullable(),
  /** Null once the event is gone. */
  venueName: z.string().nullable(),
  eventDate: calendarDaySchema,
  rating: z.number().int().min(1).max(5),
  body: z.string(),
  /** The version being decided: sent back with the decision. */
  sentAt: instantSchema,
})

export type ReviewToApproveDto = z.infer<typeof reviewToApproveDtoSchema>

/** The published review a reply or a report is about, as an admin reads it beside it. */
export const reviewAnsweredDtoSchema = z.object({
  rating: z.number().int().min(1).max(5),
  body: z.string(),
  reviewer: reviewerDtoSchema,
  month: reviewMonthSchema,
})

export type ReviewAnsweredDto = z.infer<typeof reviewAnsweredDtoSchema>

/** A company's reply waiting for approval, under the review it answers. */
export const replyToApproveDtoSchema = z.object({
  reviewId: z.uuid(),
  vendor: z.object({ id: z.uuid(), name: z.string() }),
  review: reviewAnsweredDtoSchema,
  body: z.string(),
  /** Who at the company sent it; null once their account is gone. */
  writtenBy: z.object({ name: z.string(), email: z.string() }).nullable(),
  /** The version being decided: sent back with the decision. */
  sentAt: instantSchema,
})

export type ReplyToApproveDto = z.infer<typeof replyToApproveDtoSchema>

/** A company's report of a published review, waiting for an admin to keep it or take it down. */
export const reportToSettleDtoSchema = z.object({
  reviewId: z.uuid(),
  vendor: z.object({ id: z.uuid(), name: z.string() }),
  review: reviewAnsweredDtoSchema,
  reason: z.string(),
  /** Who at the company reported it; null once their account is gone. */
  reportedBy: z.object({ name: z.string(), email: z.string() }).nullable(),
  reportedAt: instantSchema,
})

export type ReportToSettleDto = z.infer<typeof reportToSettleDtoSchema>

/** What waits for a platform admin, each kind the longest waiting first. */
export const reviewsToApproveDtoSchema = z.object({
  /** Up to `REVIEWS_TO_APPROVE_SHOWN`. */
  reviews: z.array(reviewToApproveDtoSchema),
  /** How many reviews wait in all. */
  waiting: z.number().int().nonnegative().default(0),
  /** Replies waiting, up to `REVIEWS_TO_APPROVE_SHOWN`. Defaulted while an older API is still answering. */
  replies: z.array(replyToApproveDtoSchema).default([]),
  repliesWaiting: z.number().int().nonnegative().default(0),
  /** Reports waiting, up to `REVIEWS_TO_APPROVE_SHOWN`. */
  reports: z.array(reportToSettleDtoSchema).default([]),
  reportsWaiting: z.number().int().nonnegative().default(0),
})

export type ReviewsToApproveDto = z.infer<typeof reviewsToApproveDtoSchema>

/** A published review, as the vendor's page and profile show it. Nothing else of the review is public. */
export const shownVendorReviewDtoSchema = z.object({
  id: z.uuid(),
  rating: z.number().int().min(1).max(5),
  body: z.string(),
  reviewer: reviewerDtoSchema,
  month: reviewMonthSchema,
  /** The company's published reply; null while it has none. Defaulted while an older API is still answering. */
  reply: z.object({ body: z.string() }).nullable().default(null),
})

export type ShownVendorReviewDto = z.infer<typeof shownVendorReviewDtoSchema>

/** The average over every shown review, to one decimal, and how many there are. */
export const vendorReviewSummaryDtoSchema = z.object({
  average: z.number().min(1).max(5),
  count: z.number().int().positive(),
})

export type VendorReviewSummaryDto = z.infer<typeof vendorReviewSummaryDtoSchema>

/** A company's published reviews, on its page and profile: the most recent, summed up over all. */
export const shownVendorReviewsDtoSchema = z.object({
  /** Null while there are none. */
  summary: vendorReviewSummaryDtoSchema.nullable(),
  /** The most recently published, up to `REVIEWS_SHOWN`. */
  reviews: z.array(shownVendorReviewDtoSchema),
})

export type ShownVendorReviewsDto = z.infer<typeof shownVendorReviewsDtoSchema>

/** No reviews: what a page or profile reads from an API older than reviews, mid-deploy. A new one each time. */
export function noReviews(): ShownVendorReviewsDto {
  return { summary: null, reviews: [] }
}

/** One of the company's own published reviews, on its Reviews page. */
export const ownVendorReviewDtoSchema = shownVendorReviewDtoSchema.extend({
  publishedAt: instantSchema,
  /** Its reply as the company sent it, wherever it stands; null while it has none. */
  myReply: z
    .object({
      body: z.string(),
      status: vendorReviewReplyStatusSchema,
      /** Why an admin declined it; null unless declined. */
      declineReason: z.string().nullable(),
      sentAt: instantSchema,
    })
    .nullable()
    .default(null),
  /** Its report, once made: why, and whether an admin has kept the review since. */
  report: z.object({ reason: z.string(), reportedAt: instantSchema, kept: z.boolean() }).nullable().default(null),
})

export type OwnVendorReviewDto = z.infer<typeof ownVendorReviewDtoSchema>

/** The company's published reviews, newest first, a page at a time. */
export const ownVendorReviewListDtoSchema = z.object({
  summary: vendorReviewSummaryDtoSchema.nullable(),
  reviews: z.array(ownVendorReviewDtoSchema),
  total: z.number().int().nonnegative(),
  /** Whether the reader may reply and report: the company's owners. */
  canAnswer: z.boolean().default(false),
})

export type OwnVendorReviewListDto = z.infer<typeof ownVendorReviewListDtoSchema>
